import { LatLngTuple } from "leaflet";

const gbfsStationInformationUrl = "https://gbfs.nextbike.net/maps/gbfs/v2/nextbike_zz/pl/station_information.json";
const gbfsStationStatusUrl = "https://gbfs.nextbike.net/maps/gbfs/v2/nextbike_zz/pl/station_status.json";
const nominatimReverseUrl = "https://nominatim.openstreetmap.org/reverse";
const metroRowerAddressCacheKey = "metrorower-address-cache-v1";

type GbfsStationInformation = {
  station_id: string;
  name: string;
  short_name?: string;
  lat: number;
  lon: number;
  capacity?: number;
  is_virtual_station?: boolean;
  rental_uris?: {
    web?: string;
    android?: string;
    ios?: string;
  };
};

type GbfsStationStatus = {
  station_id: string;
  num_bikes_available?: number;
  num_docks_available?: number;
  is_installed?: boolean;
  is_renting?: boolean;
  is_returning?: boolean;
  last_reported?: number;
};

export type MetroRowerStation = {
  stationId: string;
  name: string;
  location: LatLngTuple;
  ref?: string;
  capacity?: string;
  operator?: string;
  website?: string;
  gbfsInfo?: GbfsStationInformation;
  gbfsStatus?: GbfsStationStatus;
};

export type MetroRowerAddressState = {
  address: string | null;
  loading: boolean;
};

type NominatimReverseResponse = {
  address?: {
    city?: string;
    footway?: string;
    house_number?: string;
    neighbourhood?: string;
    pedestrian?: string;
    postcode?: string;
    quarter?: string;
    road?: string;
    suburb?: string;
    town?: string;
    village?: string;
  };
  display_name?: string;
};

async function fetchGbfsJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`GBFS request failed: ${response.status}`);
  }
  return response.json();
}

function buildStatusMap(statuses: GbfsStationStatus[]): Map<string, GbfsStationStatus> {
  return new Map(statuses.map((status) => [status.station_id, status]));
}

function formatLastReported(lastReported?: number): string | null {
  if (!lastReported) {
    return null;
  }

  return new Date(lastReported * 1000).toLocaleString("pl-PL");
}

function formatResolvedAddress(response: NominatimReverseResponse): string | null {
  if (!response.address) {
    return response.display_name || null;
  }

  const street =
    response.address.road ||
    response.address.pedestrian ||
    response.address.footway ||
    response.address.neighbourhood ||
    response.address.quarter;
  const streetLine = [street, response.address.house_number].filter(Boolean).join(" ");
  const city =
    response.address.city ||
    response.address.town ||
    response.address.village ||
    response.address.suburb;
  const localityLine = [response.address.postcode, city].filter(Boolean).join(" ");
  const formatted = [streetLine, localityLine].filter(Boolean).join(", ");

  return formatted || response.display_name || null;
}

function buildAddressLine(addressState?: MetroRowerAddressState): string {
  if (!addressState || addressState.loading) {
    return [
      "Adres: ",
      '<span class="metrorower-popup__address-loading">',
      '<span class="metrorower-popup__loader" aria-hidden="true"></span>',
      "ładowanie adresu...",
      "</span>"
    ].join("");
  }

  if (addressState.address) {
    return `Adres: ${addressState.address}`;
  }

  return "Adres: brak danych";
}

function buildPopupDescription(
  station: MetroRowerStation,
  addressState?: MetroRowerAddressState
): string {
  const lines = [
    `<strong>${station.name}</strong>`,
    `<small>stacja roweru miejskiego Metrorower</small>`,
    buildAddressLine(addressState)
  ];

  if (station.ref) {
    lines.push(`Numer stacji: ${station.ref}`);
  }
  if (station.capacity) {
    lines.push(`Pojemność: ${station.capacity}`);
  }
  if (station.operator) {
    lines.push(`Operator: ${station.operator}`);
  }
  if (station.gbfsStatus) {
    const bikes = station.gbfsStatus.num_bikes_available ?? 0;
    const docks = station.gbfsStatus.num_docks_available ?? 0;
    lines.push(`Dostępne rowery: ${bikes}`);
    lines.push(`Wolne miejsca: ${docks}`);
    lines.push(`Wypożyczanie: ${station.gbfsStatus.is_renting ? "tak" : "nie"}`);
    lines.push(`Zwrot: ${station.gbfsStatus.is_returning ? "tak" : "nie"}`);
    const lastReported = formatLastReported(station.gbfsStatus.last_reported);
    if (lastReported) {
      lines.push(`Aktualizacja: ${lastReported}`);
    }
  }
  if (station.website) {
    lines.push(`<a href="${station.website}" target="_blank" rel="noreferrer">Strona stacji</a>`);
  } else if (station.gbfsInfo?.rental_uris?.web) {
    lines.push(`<a href="${station.gbfsInfo.rental_uris.web}" target="_blank" rel="noreferrer">Otwórz w Metrorower</a>`);
  }

  return lines.join("<br>\n");
}

async function getMetroRowerStations(): Promise<MetroRowerStation[]> {
  const [stationInformationResult, stationStatusResult] = await Promise.allSettled([
    fetchGbfsJson<{ data: { stations: GbfsStationInformation[] } }>(gbfsStationInformationUrl),
    fetchGbfsJson<{ data: { stations: GbfsStationStatus[] } }>(gbfsStationStatusUrl)
  ]);

  const gbfsStations =
    stationInformationResult.status === "fulfilled"
      ? stationInformationResult.value.data.stations
      : [];
  const gbfsStatusMap =
    stationStatusResult.status === "fulfilled"
      ? buildStatusMap(stationStatusResult.value.data.stations)
      : new Map<string, GbfsStationStatus>();

  return gbfsStations
    .map((station) => ({
      stationId: station.station_id,
      name: station.name || "Stacja Metrorower",
      location: [station.lat, station.lon] as LatLngTuple,
      ref: station.short_name,
      capacity: station.capacity?.toString(),
      operator: "Metrorower / Nextbike",
      website: station.rental_uris?.web,
      gbfsInfo: station,
      gbfsStatus: gbfsStatusMap.get(station.station_id),
    }))
    .filter((station) => station.gbfsStatus?.is_installed !== false)
    .sort((a, b) => a.name.localeCompare(b.name, "pl"));
}

export async function getNextBike(): Promise<MetroRowerStation[]> {
  try {
    return await getMetroRowerStations();
  } catch {
    return [];
  }
}

export function getMetroRowerStationKey(station: MetroRowerStation): string {
  return station.stationId || station.ref || `${station.location[0]}-${station.location[1]}`;
}

export function readMetroRowerAddressCache(): Record<string, string> {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const savedCache = window.localStorage.getItem(metroRowerAddressCacheKey);
    if (!savedCache) {
      return {};
    }

    return JSON.parse(savedCache) as Record<string, string>;
  } catch {
    return {};
  }
}

export function writeMetroRowerAddressCache(cache: Record<string, string>) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(metroRowerAddressCacheKey, JSON.stringify(cache));
  } catch {
    ;
  }
}

export async function fetchMetroRowerAddress(location: LatLngTuple): Promise<string | null> {
  try {
    const response = await fetch(
      `${nominatimReverseUrl}?format=jsonv2&lat=${location[0]}&lon=${location[1]}&zoom=18&addressdetails=1&accept-language=pl`,
      {
        headers: {
          Accept: "application/json"
        }
      }
    );

    if (!response.ok) {
      return null;
    }

    const data = await response.json() as NominatimReverseResponse;
    return formatResolvedAddress(data);
  } catch {
    return null;
  }
}

export function buildMetroRowerPopup(
  station: MetroRowerStation,
  addressState?: MetroRowerAddressState
): string {
  return buildPopupDescription(station, addressState);
}
